import { PopOverMenuOption } from 'Components/shared/PopOverMenuOption';
import { COMMUNICATOR_MARK_AS_UNREAD } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { get } from 'lodash';
import { MobXProviderContext, observer } from 'mobx-react';
import {
  useMarkConversationAsRead,
  useMarkConversationAsUnread,
} from 'Modules/conversation/conversations-list/requests/index.requests';
import { useDeleteConversationModal } from 'Modules/conversation/deleteConversation/hooks/useDeleteConversation';
import {
  canShowMarkAsReadUnread,
  canShowMuteUnmute,
} from 'Modules/conversation/guards/conversationActionChecks';
import { useIsPersonInactive } from 'Modules/conversation/hooks/useIsPersonInactive';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import React, { useCallback } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { bugsnagClient } from 'Utils/logUtils';
import { selectProperVideoTooltipMessageForOneOnOnes } from 'Utils/videoConferenceUtils';
import { Styled } from './index.styles';
import { MoreActivityButtonProps } from './interface';

const MoreActivityButton: React.FC<MoreActivityButtonProps> = (props) => {
  const {
    ui: uiStore,
    conversation: conversationStore,
    config: configStore,
    person: personStore,
    participant: participantStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);
  const { getIsConversationPinned, isPinningOrUnpinning } =
    useConversationStore();

  const { [COMMUNICATOR_MARK_AS_UNREAD]: isMarkAsUnreadEnabled } =
    useFeatureFlags([COMMUNICATOR_MARK_AS_UNREAD]);

  const { mutate: markConversationAsRead } = useMarkConversationAsRead();
  const { mutate: markConversationAsUnread } = useMarkConversationAsUnread();

  const isInactive = useIsPersonInactive(props.conversation);

  const isConversationPinned = getIsConversationPinned(props.conversation.id);

  const conversationUnreadCounts = uiStore.selectConversationUnreadCounts(
    props.conversation.id
  );

  const isConversationUnread = conversationUnreadCounts.unreadMessages > 0;

  const conversationMuteToggle = useCallback(() => {
    const { conversation, currentPersonId, toggleMuteStatus } = props;

    if (!currentPersonId) {
      toggleMuteStatus(conversation?.id);
      return;
    }

    void conversationStore
      .loadOrCreateConversationWithPost(currentPersonId)
      .then((resp) => {
        toggleMuteStatus(resp.data.id?.toString());
      });
  }, [conversationStore, props]);

  const placeVideoCallConference = useCallback(() => {
    const { conversation, currentPersonId } = props;
    props.onClose?.();
    if (currentPersonId === null || currentPersonId === undefined) {
      void conversationStore.postConferenceByConversationId(conversation.id);
      return;
    }

    void conversationStore
      .loadOrCreateConversationWithPost(currentPersonId)
      .then((resp) => {
        void conversationStore.postConferenceByConversationId(
          resp.data.id.toString()
        );
      });
  }, [conversationStore, props]);

  const handlePinConversation = useCallback(() => {
    props.setIsConversationPinned(props.conversation.id);
  }, [props]);

  const handleMarkAsRead = useCallback(
    (e) => {
      e.stopPropagation();
      markConversationAsRead({
        conversationId: props.conversation.id,
        source: 'list',
      });
      props.onClose?.();
    },
    [markConversationAsRead, props]
  );

  const handleMarkAsUnread = useCallback(
    (e) => {
      e.stopPropagation();
      markConversationAsUnread({
        conversationId: props.conversation.id,
        source: 'list',
      });
      props.onClose?.();
    },
    [markConversationAsUnread, props]
  );

  const {
    personPbo,
    conversation,
    toggleConfirmationModal,
    showMoreActionOptions,
  } = props;

  const otherParticipants =
    participantStore.selectOtherParticipants(conversation.id) ?? [];

  const isMuted = uiStore.selectIfConvMuted(conversation.id);

  const showMarkAsReadUnread = canShowMarkAsReadUnread(
    conversation,
    isMarkAsUnreadEnabled
  );
  const showMuteUnmute = canShowMuteUnmute(conversation);

  const { handleDeleteConversation } = useDeleteConversationModal({
    conversation: props.conversation,
    onClose: props.onClose,
    onError: (error) => {
      bugsnagClient.notify(error as Error, (event) => {
        event.severity = 'error';
        event.context = 'MoreActivityButtons.handleDeleteConversation';
        event.addMetadata('custom', {
          function: 'handleDeleteConversation',
          action: 'delete_conversation_attempt',
          conversationId: props.conversation.id,
          code: error,
        });
        event.request = error;
      });
    },
  });

  // Only display video call option if there are any participants other than the logged in user and the other participant is not inactive
  const renderStartConferenceOption = () => {
    const hasOtherParticipants = otherParticipants.length > 0;
    const isVideoEnabled = personStore.personAvaliableFeatures.video.enabled;
    const isConfigFulfilled =
      configStore &&
      get(configStore, 'signedInPersonConfig.state') === 'fulfilled';
    const isVideoEnabledInConfig = get(
      configStore,
      'signedInPersonConfig.value.data.video.enabled',
      false
    );

    if (!hasOtherParticipants || isInactive) {
      return null;
    }

    const startConferenceOption = (
      <PopOverMenuOption
        testid={`${conversation.id}-startConference`}
        icon="video"
        disabled={!isVideoEnabled}
        onClick={isVideoEnabled ? placeVideoCallConference : null}
      >
        {selectProperVideoTooltipMessageForOneOnOnes(null, null, null)}
      </PopOverMenuOption>
    );

    if (personPbo) {
      return personPbo.case({
        pending: () => <>Loading...</>,
        rejected: (reason) => (
          <span>{get(reason, 'response.data.message')}</span>
        ),
        fulfilled: () =>
          isVideoEnabled && !conversation.IsSmsConversation
            ? startConferenceOption
            : null,
      });
    }

    if (
      isConfigFulfilled &&
      isVideoEnabledInConfig &&
      !conversation.IsSmsConversation
    ) {
      return startConferenceOption;
    }

    return null;
  };

  return showMoreActionOptions ? (
    <Styled.Container>
      {renderStartConferenceOption()}

      {showMarkAsReadUnread && (
        <PopOverMenuOption
          testid={`${conversation.id}-${
            isConversationUnread ? 'markAsRead' : 'markAsUnread'
          }`}
          icon={isConversationUnread ? 'message-read' : 'message'}
          onClick={isConversationUnread ? handleMarkAsRead : handleMarkAsUnread}
        >
          {isConversationUnread ? 'Mark as read' : 'Mark as unread'}
        </PopOverMenuOption>
      )}

      {showMuteUnmute && (
        <PopOverMenuOption
          testid={`${conversation.id}-${isMuted ? 'unmute' : 'mute'}`}
          icon={isMuted ? 'bell' : 'bell-off'}
          onClick={conversationMuteToggle}
        >
          {`${isMuted ? 'Unmute' : 'Mute'} notifications`}
        </PopOverMenuOption>
      )}

      {props.setIsConversationPinned && !conversation.archived && (
        <PopOverMenuOption
          testid={`${conversation.id}-pinConversation`}
          icon={isConversationPinned ? 'pin-off' : 'pin'}
          onClick={handlePinConversation}
          disabled={isPinningOrUnpinning}
        >
          {isConversationPinned ? 'Unpin conversation' : 'Pin conversation'}
        </PopOverMenuOption>
      )}

      <PopOverMenuOption
        testid={`${conversation.id}-${
          conversation.archived ? 'unarchive' : 'archive'
        }Conversation`}
        icon={conversation.archived ? 'unarchive' : 'archive'}
        onClick={toggleConfirmationModal}
      >
        {conversation.archived
          ? 'Unarchive conversation'
          : 'Archive conversation'}
      </PopOverMenuOption>

      <PopOverMenuOption
        testid={`${conversation.id}-deleteConversation`}
        icon="delete"
        destructive
        onClick={handleDeleteConversation}
      >
        Delete conversation
      </PopOverMenuOption>
    </Styled.Container>
  ) : (
    <></>
  );
};

export default observer(MoreActivityButton);
